import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { EASE } from "./shared";

const MESSAGE = encodeURIComponent("Hello, I'd like to know more about the ELYNCOR ELEMENTS 7 Chakra Crystal Tree.");
const FOUNDERS = [
  { name: "Daksh", number: "917733011368", display: "+91 7733011368" },
  { name: "Shivam", number: "918076601010", display: "+91 8076601010" },
];

export default function WhatsAppButton() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="fixed bottom-5 right-5 sm:bottom-8 sm:right-8 z-[60] flex flex-col items-end gap-3" data-testid="whatsapp-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="w-[260px] rounded-sm border border-[#1B1915]/12 bg-[#FDFBF7]/95 backdrop-blur-xl shadow-[0_24px_60px_-20px_rgba(27,25,21,0.35)] p-5"
            data-testid="whatsapp-popover"
          >
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#8C7A56]/85 mb-4">Message us on WhatsApp</p>
            <div className="divide-y divide-[#1B1915]/10">
              {FOUNDERS.map((f) => (
                <a
                  key={f.name}
                  href={`https://wa.me/${f.number}?text=${MESSAGE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="group flex items-center justify-between py-3.5 transition-colors duration-300"
                  data-testid={`whatsapp-link-${f.name.toLowerCase()}`}
                >
                  <span>
                    <span className="block font-serif-display text-lg text-[#1B1915] leading-none">{f.name}</span>
                    <span className="block mt-1.5 text-[12px] text-[#1A201C]/55 tracking-wide">{f.display}</span>
                  </span>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#25A45A] opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-300">
                    Chat
                  </span>
                </a>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-[#1A201C]/45">Usually replies within a day.</p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        transition={{ duration: 0.25, ease: EASE }}
        aria-label={open ? "Close WhatsApp options" : "Message us on WhatsApp"}
        aria-expanded={open}
        className="relative w-14 h-14 rounded-full bg-[#1B1915] text-[#FDFBF7] shadow-[0_18px_40px_-14px_rgba(27,25,21,0.6)] flex items-center justify-center"
        data-testid="whatsapp-fab"
      >
        {!open && <span className="absolute inset-0 rounded-full bg-[#25A45A]/35 animate-ping [animation-duration:2.4s]" />}
        <span className="absolute inset-0 rounded-full ring-1 ring-[#25A45A]/60" />
        {open ? <X className="w-5 h-5 relative" strokeWidth={1.6} /> : <MessageCircle className="w-6 h-6 relative" strokeWidth={1.6} />}
      </motion.button>
    </div>
  );
}
